<template>
  <div class="card">
    <div v-if="selectedQuestion">
      <nav-bar v-if="!isDedicated" :back="true" :right="goNextLink"></nav-bar>
      <div v-if="canShowForm && (!userHasVoted || !!displayResultsAfterVote) && isActive">
        <question-form
          :poll="selectedPoll"
          :question="selectedQuestion"
          :answers="selectedQuestion.answers"
          :questionStats="statsOfSelectedQuestion"
          :displayCorrectAnswer="displayResultsAfterVote" 
          @answersSelected="answersSelected"
        />
        <nav-bar v-if="duplicateQuestionNavigation && !isDedicated" :back="true" :right="goNextLink"></nav-bar>
        <question-form-submit-button @submit="vote" :disabled="selectedAnswersIds.length === 0" />
      </div>

      <div v-if="userHasVoted && isDisplayed && !displayResultsAfterVote">
        <question-results
          v-if="statsOfSelectedQuestion"
          :poll="selectedPoll"
          :question="selectedQuestion"
          :questionStats="statsOfSelectedQuestion"
        ></question-results>
        <question-form-submitted :question="selectedQuestion" v-else />
        <nav-bar v-if="duplicateQuestionNavigation && !isDedicated" :back="true" :right="goNextLink"></nav-bar>
      </div>
      <div v-if="!userHasVoted && isDisplayed && !isActive && !displayResultsAfterVote">
        <!-- not voted user allowed to see results -->
        <!-- <question-not-voted /> -->
        <question-results
          v-if="statsOfSelectedQuestion"
          :poll="selectedPoll"
          :question="selectedQuestion"
          :questionStats="statsOfSelectedQuestion"
        ></question-results>
        <nav-bar v-if="duplicateQuestionNavigation && !isDedicated" :back="true" :right="goNextLink"></nav-bar>
      </div>
      <div v-else-if="userHasVoted && !isDisplayed && !displayResultsAfterVote">
        <question-form-submitted :question="selectedQuestion" />
        <nav-bar v-if="duplicateQuestionNavigation && !isDedicated" :back="true" :right="goNextLink"></nav-bar>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { isEmpty, findIndex, propEq } from 'ramda';
import NavBar from '@/components/navigation/NavBar';
import QuestionForm from '@/components/questions/QuestionForm';
import QuestionResults from '@/components/questions/QuestionResults';
import QuestionFormSubmitButton from '@/components/questions/QuestionFormSubmitButton';
import QuestionFormSubmitted from '@/components/questions/QuestionFormSubmitted';
import QuestionNotVoted from '@/components/questions/QuestionNotVoted';

const searchParams = new URLSearchParams(window.location.search);
const duplicateNavigation = searchParams.get('duplicate-question-links') === 'true';

export default {
  name: 'question-content',
  components: {
    NavBar,
    QuestionForm,
    QuestionFormSubmitButton,
    QuestionFormSubmitted,
    QuestionResults,
    QuestionNotVoted
  },
  data: () => ({
    selectedAnswersIds: [],
    fetchStatsInterval: null,
    fetchStatsEveryMs: 2 * 1000,
    displayResultsAfterVoteTimeout: null,
    displayResultsAfterVote: false
  }),
  computed: {
    ...mapGetters([
      'selectedPoll',
      'selectedQuestion',
      'questionsOfSelectedPoll',
      'statsOfSelectedQuestion',
      'userVotes',
      'isDedicated'
    ]),
    ...mapGetters({ votedAll: 'userHasVotedAll', userScore: 'userScore', totalScore: 'totalScore'}),
    canShowForm() {
      return !isEmpty(this.selectedPoll) && !isEmpty(this.selectedQuestion);
    },
    userHasVoted() {
      return (this.userVotes[this.selectedPoll.pollId] || []).includes(
        this.selectedQuestion.questionId
      );
    },
    isActive() {
      return !isEmpty(this.selectedPoll) && this.selectedPoll.isActive;
    },
    isDisplayed() {
      return !isEmpty(this.selectedPoll) && (this.selectedPoll.isDisplayed);
    },
    goNextLink() {
      if (this.selectedPoll.type === 'FORM') {
        const questionIndex = findIndex(propEq('questionId', this.selectedQuestion.questionId))(
          this.questionsOfSelectedPoll
        );
        const nextQuestion = this.questionsOfSelectedPoll[questionIndex + 1];
        if (nextQuestion) {
          return {
            text: this.$t('NAVIGATION.NEXT_QUESTION'),
            route: 'questionContent',
            params: {
              pollId: this.selectedPoll.pollId,
              questionId: nextQuestion.questionId
            }
          };
        }
      }
      return null;
    }, 
    duplicateQuestionNavigation() {
      return duplicateNavigation;
    }
  },
  watch: {
    $route(to) {
      clearInterval(this.fetchStatsInterval);
      this.selectQuestion(to.params.questionId);
      clearTimeout(this.displayResultsAfterVoteTimeout);
      this.displayResultsAfterVote = false;
      this.selectedAnswersIds = [];
    },
    userHasVoted(hasVoted) {
      // if the user has voted this question, we want to display
      // the results, so we need to fetch stats
      if (hasVoted) {
        clearInterval(this.fetchStatsInterval);
        this.fetchStatsOfPoll({ pollId: this.selectedPoll.pollId });
        this.fetchStatsInterval = setInterval(() => {
          this.fetchStatsOfPoll({ pollId: this.selectedPoll.pollId });
        }, this.fetchStatsEveryMs);
      }
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      const pollId = vm.$route.params.pollId;
      // fetch poll stats to allow not voted user to see results
      vm.fetchStatsOfPoll({ pollId });
      vm.fetchUserVotes(pollId);
      vm.selectPoll(pollId).then(() => {
        return vm.selectQuestion(vm.$route.params.questionId);
      });
    });
  },

  beforeRouteLeave(to, from, next) {
    clearInterval(this.fetchStatsInterval);
    // we need to unselect the question before leaving,
    // because otherwise when we go to another question,
    // we'll see the old question displayed before we select the current one
    this.unselectQuestion();
    next();
  },

  methods: {
    ...mapActions([
      'selectPoll',
      'selectQuestion',
      'unselectQuestion',
      'voteQuestion',
      'fetchUserVotes',
      'fetchStatsOfPoll'
    ]),

    answersSelected({ answersIds }) {
      this.selectedAnswersIds = answersIds;
    },

    vote() {
      clearTimeout(this.displayResultsAfterVoteTimeout);
      this.displayResultsAfterVote = true;
      this.displayResultsAfterVoteTimeout = setTimeout(() => {
        this.displayResultsAfterVote = false;
      }, 2000);
      this.voteQuestion({
        pollId: this.selectedPoll.pollId,
        questionId: this.selectedQuestion.questionId,
        answersIds: this.selectedAnswersIds
      }).then(() => {
        setTimeout(() => {
          this.fetchStatsOfPoll({ pollId: this.selectedPoll.pollId });
        }, 1000);
      });
    }
  }
};
</script>

<style lang="scss" scoped></style>
